// Utilities
import { defineStore } from "pinia";
import { useLocalStorage, useSessionStorage } from "@vueuse/core";
import { AbilityTuple, MongoAbility, MongoQuery } from "@casl/ability";
import userStore from "./user";

// Formato mínimo que `filtrarRotasComAcesso`, `filtraRota` e `separarPorGrupos`
// precisam da rota — não é o tipo de rota do vue-router (isso reintroduziria o
// import de `router/`, ver AD-018). Quem chama a action (hoje só
// `src/router/helpers.ts`) passa as rotas reais; esse tipo só descreve o que é
// lido aqui dentro.
type RotaComLayout = {
  layoutNome?: string;
  children?: RotaComLayout[];
  meta?: {
    showMenu?: boolean;
    subject?: unknown;
    action?: unknown;
    grupo?: string;
    grupoTitle?: string;
  };
  [chave: string]: unknown;
};

/** Temporizador do apagar do indicador discreto — um por aplicacao, nao por instancia da store. */
let temporizadorDiscreto: ReturnType<typeof setTimeout> | null = null;

export default defineStore("layout", {
  state: () => ({
    _railMenu: useSessionStorage("railMenu", false),
    _breadcrumbs: {},
    _loading: false,
    _loadingControle: false,
    _loadClass: "",
    _tempo: {},
    _wait: false,
    _sidebarOpen: false,

    // `_linksMenu*` alimenta LayoutObra.vue/LayoutGeral.vue/LayoutFabricacao.vue
    // (fora do escopo da T123) sem contrato de tipo publicado — tipar aqui
    // exigiria mexer nos três consumidores.
    /* eslint-disable @typescript-eslint/no-explicit-any -- ver comentário acima */
    _linksMenuObra: [] as any[],
    _linksMenuGeral: [] as any[],
    _linksMenuFabricacao: [] as any[],
    /* eslint-enable @typescript-eslint/no-explicit-any */
    _executandoAcao: false,
    _msgCarregamento: "",
    _redirectTo: useLocalStorage("redirectTo", {}),

    // Indicador discreto (port de origin/corrigir-tipos-projeto 7fd10c0f): canal de
    // carregamento que nao bloqueia a tela, separado de `_executandoAcao`.
    _executandoDiscreto: false,
    _executandoDiscretoSpinning: false,
    _executandoDiscretoResponse: true,
  }),

  actions: {
    setBreadcrumbs(breadcrumbsP: unknown[]) {
      this._breadcrumbs = breadcrumbsP as never;
    },
    setRail(rail: boolean) {
      this._railMenu = rail;
      return this._railMenu;
    },
    // ESSE LOADING É O QUE ATIVA A ANIMAÇÃO DO LOGO, QUANDO USAR? NAÕ SEI
    setLoading(status: boolean) {
      this._loadingControle = status;
      if (this._wait) {
        this._loadClass = "";
        return;
      }

      this._wait = true;
      this._loadClass = "";
      this._loading = status;
      setTimeout(() => {
        this._wait = false;

        if (!this._loadClass) this._loadClass += " saindo";

        window.setTimeout(() => {
          this._loading = this._loadingControle;
          this._loadClass = "";
        }, 500);
      }, 4000);
    },

    filtrarRotasComAcesso(rotasAbertas: RotaComLayout[], rotasRestritas: RotaComLayout[]) {
      const rotasRestritasObra = rotasRestritas.filter((rota) => {
        if (rota.layoutNome == "LayoutObra") {
          return rota.children;
        }
      });
      const rotasRestritasGerais = rotasRestritas.filter((rota) => {
        if (rota.layoutNome == "LayoutGeral") {
          return rota.children;
        }
      });
      const rotasAbertasGerais = rotasAbertas.filter((rota) => {
        if (rota.layoutNome == "LayoutGeralAberto") {
          return rota.children;
        }
      });
      const rotasRestritasFabricacao = rotasRestritas.filter((rota) => {
        if (rota.layoutNome == "LayoutFabricacao") {
          return rota.children;
        }
      });
      this._linksMenuFabricacao = separarPorGrupos([...filtraRota(rotasRestritasFabricacao)]);
      this._linksMenuObra = separarPorGrupos([...filtraRota(rotasRestritasObra)]);
      this._linksMenuGeral = separarPorGrupos([
        ...filtraRota(rotasRestritasGerais),
        ...filtraRota(rotasAbertasGerais),
      ]);
    },

    setExecutando(status: boolean, msg: string = "") {
      this._msgCarregamento = status ? msg : "";
      this._loadingControle = status;
      if (this._wait) {
        this._msgCarregamento = status ? msg : "Finalizando...";
        return;
      }

      this._wait = true;
      this._executandoAcao = status;
      setTimeout(() => {
        this._wait = false;
        window.setTimeout(() => {
          this._executandoAcao = this._loadingControle;
        }, 500);
      }, 1000);
    },

    /**
     * Liga/desliga o indicador discreto. `status: true` acende o circulo com o spinner;
     * `status: false` para o spinner, registra se deu certo (`response`) e apaga o
     * circulo 2400 ms depois — tempo para o icone de resultado ser lido. Uma operacao
     * nova nesse intervalo cancela o apagar.
     */
    setExecutandoDiscreto(status: boolean, response: boolean = true) {
      if (status) {
        if (temporizadorDiscreto) {
          clearTimeout(temporizadorDiscreto);
          temporizadorDiscreto = null;
        }
        this._executandoDiscreto = true;
        this._executandoDiscretoSpinning = true;
        return;
      }
      this._executandoDiscretoSpinning = false;
      this._executandoDiscretoResponse = response;
      temporizadorDiscreto = setTimeout(() => {
        this._executandoDiscreto = false;
        temporizadorDiscreto = null;
      }, 2400);
    },

    setMensagemCarregamento(msg: string) {
      this._msgCarregamento = msg;
    },
    setRedirect(to: Record<string, unknown>) {
      this._redirectTo = to;
    },
    setSidebar(state: boolean) {
      this._sidebarOpen = state;
    },
  },

  getters: {
    breadcrumbs: (state) => {
      return state._breadcrumbs || [];
    },
    loading: (state) => {
      return state._loading;
    },
    loadClass: (state) => {
      return state._loadClass;
    },
    railMenu: (state) => {
      return state._railMenu;
    },
    executandoAcao: (state) => {
      return state._executandoAcao;
    },
    msgCarregamento: (state) => {
      return state._msgCarregamento;
    },
    redirectTo: (state) => {
      return state._redirectTo;
    },
    isSidebarOpen: (state) => {
      return state._sidebarOpen;
    },
    executandoDiscreto: (state) => state._executandoDiscreto,
    executandoDiscretoSpinning: (state) => state._executandoDiscretoSpinning,
    executandoDiscretoResponse: (state) => state._executandoDiscretoResponse,
  },
});

function filtraRota(todasRotas: RotaComLayout[], isRestrita = true): RotaComLayout[] {
  let rotas: RotaComLayout[] = [];
  todasRotas.forEach((rota) => {
    if (rota.meta?.showMenu) {
      if (isRestrita) {
        if (possuiAcesso(userStore().ability, rota.meta?.subject, rota.meta?.action)) {
          rotas.push(rota);
        }
      } else {
        rotas.push(rota);
      }
    }
    if (rota.children) {
      rotas = rotas.concat(filtraRota(rota.children, isRestrita));
    }
  });
  return rotas;
}

function separarPorGrupos(rotas: RotaComLayout[]) {
  const grupos: Record<string, { title?: string; items: RotaComLayout[]; eh_grupo: true }> = {};
  const items: unknown[] = [];
  rotas.forEach((item) => {
    if (item.meta?.showMenu) {
      if (item.meta && Object.hasOwn(item.meta, "grupo")) {
        const grupo = item.meta.grupo as string;
        if (Object.hasOwn(grupos, grupo)) {
          grupos[grupo].items.push(item);
        } else {
          grupos[grupo] = {
            title: item.meta.grupoTitle,
            items: [item],
            eh_grupo: true,
          };
        }
      } else {
        items.push({
          ...item,
          eh_grupo: false,
        });
      }
    }
  });

  Object.keys(grupos).forEach((grupo) => {
    items.push(grupos[grupo]);
  });
  return items;
}

function possuiAcesso(ability: unknown, rolesNecessarios: unknown, acoes: unknown = []) {
  const casl = ability as MongoAbility<AbilityTuple, MongoQuery> | null | undefined;
  if (casl) {
    const roles = rolesNecessarios as string[] | undefined;
    const listaAcoes = acoes as string[];
    if (roles && roles.length) {
      let liberado = false;
      let i = 0;
      do {
        if (listaAcoes.length) {
          for (const acao of listaAcoes) {
            liberado = casl.can(acao, roles[i]);
          }
        } else {
          liberado = casl.can("read", roles[i]);
        }
        i = i + 1;
      } while (!liberado && i < roles.length);
      return liberado;
    }
    return true;
  }
  return false;
}
